/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { connect, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";

import Pagination from "rc-pagination";

import {
  CheckboxVisibility,
  CommandBar,
  DefaultButton,
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IconButton,
  IColumn,
  ICommandBarItemProps,
  IDetailsRowProps,
  Link,
  Modal,
  Selection,
  SelectionMode,
  Spinner,
  SpinnerSize,
  Text,
} from "@fluentui/react";

import * as ApplicationActions from "Dataflows/Application/ApplicationAction";
import * as ActivitiesEventsActions from "Dataflows/ApplicationDetails/ApplicationDetailsAction";

import { useAuth } from "Authentication/AuthProvider";
import { ApplicationBulkAssignSidePanel } from "Components/ApplicationBulkAssignSidePanel/ApplicationBulkAssignSidePanel";
import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import {
  detailListStyles,
  iconButtonStyles,
  modalHeaderStyles,
  modalStyles,
  spinnerStyles,
  ModalContent,
  ModalHeader,
  StyledSpan,
} from "Components/Common/SharedListStyles.styles";
import StatusElement from "Components/Common/Status/StatusElement";
import { SortingColumns } from "Constants/AdminConstants";
import { APPLICATION_REVIEW_STATUS_START_REVIEW } from "Constants/ApplicationConstants";
import { ApplicationStatusTypesEnum } from "Constants/ApplicationStatusTypes";
import { ApplicationReviewStatusesEnum, RolesEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  getCurrentPage,
  getLoadApplicationsError,
  getPageSize,
  getSearchText,
  isLoadingApplications,
} from "Dataflows/Application/ApplicationSelector";
import { AppState } from "Dataflows/Reducers";
import { IApplication } from "Interfaces/Application/IApplication";
import { IApplicationListRowData } from "Interfaces/Application/IApplicationListRowData";
import { IApplicationUserProfileInformation } from "Interfaces/Application/IApplicationUserProfileInformation";
import { getProfileInformationByApplicationId } from "Services/ApplicationApi";
import { formatDateEu } from "Utility/FormatHelper";

import ApplicationDetails from "../ApplicationDetails/ApplicationDetails";
import { PaginationRow } from "../Common/Pagination/Pagination.styles";
import "../Common/Pagination/Pagination.styles.css";
import { activeReviewStatusButtonStyles, reviewStatusButtonStyles } from "./ApplicationList.styles";
import { IApplicationsListProps } from "./IApplicationsListProps";

type ApplicationColumn = Partial<IColumn & { tenants: string[] }>;

/**
 * List of applications
 * @Component
 * @returns {React.FunctionComponent<IApplicationsListProps>}
 */
const ApplicationsList: React.FunctionComponent<IApplicationsListProps> = (
  props: IApplicationsListProps
) => {
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userProfileInfoResults, setUserProfileInfoResults] = useState<
    IApplicationUserProfileInformation[]
  >([]);
  const { hasRole } = useAuth();
  const dispatch = useDispatch();
  const [isAssignPanelOpen, setIsAssignPanelOpen] = useState(false);

  const {
    actions,
    data,
    isLoadingApplications,
    loadApplicationsError,
    pageSize,
    filteredCount,
    currentPage,
    tenant,
  } = props;

  const userProfileInfoResultsSelected = userProfileInfoResults.filter((x) => x["isSelected"]);
  const isAssignEnabled = (): boolean => {
    // Disable the button if no profiles are selected
    if (userProfileInfoResultsSelected.length === 0) {
      return false;
    }
    const today = new Date(); // new Date();

    // Normalize the dates to remove the time component
    const normalizeDate = (date: Date): Date =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const normalizedToday = normalizeDate(today);

    const allRenewalApplicationSame = userProfileInfoResultsSelected.every(
      (x) => x.isRenewalApplication === userProfileInfoResultsSelected[0].isRenewalApplication
    );
    if (!allRenewalApplicationSame) {
      return false;
    }
    // Check if any selected user profile does not meet the condition
    const anyConditionNotMet = userProfileInfoResultsSelected.some((x) => {
      const normalizedEnrollmentEndDate = x.enrollmentEndDate
        ? normalizeDate(new Date(x.enrollmentEndDate))
        : null;

      return (
        (!hasRole(RolesEnum.MVP_PROGRAM_OWNER) && !hasRole(RolesEnum.MVP_TECH_AREA_VALIDATOR)) ||
        x.reviewStatus !== ApplicationReviewStatusesEnum.PG_REVIEW ||
        !(
          x.formStatus === ApplicationStatusTypesEnum.Submitted ||
          x.formStatus === ApplicationStatusTypesEnum.UNDER_REVIEW ||
          x.formStatus === ApplicationStatusTypesEnum.REVIEW_ELIGIBLE
        ) ||
        x.isAnnualException ||
        !(normalizedEnrollmentEndDate && normalizedEnrollmentEndDate <= normalizedToday)
      );
    });

    return !anyConditionNotMet;
  };

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: "assignApplication",
      text: "Assign",
      iconProps: { iconName: "Assign" },
      disabled: !isAssignEnabled(),
      onClick: (_, item) => setIsAssignPanelOpen(true),
    },
  ];

  /**
   * A function that renders a button element for the
   * review status of the application.
   * @param {string} reviewStatus The review status of the application
   * @returns {React.ReactElement} A button element that renders the current application status
   */
  const formatReviewStatus = (reviewStatus: string): React.ReactElement => (
    <DefaultButton
      disabled={reviewStatus !== APPLICATION_REVIEW_STATUS_START_REVIEW}
      styles={
        reviewStatus === APPLICATION_REVIEW_STATUS_START_REVIEW
          ? activeReviewStatusButtonStyles
          : reviewStatusButtonStyles
      }
      text={reviewStatus}
    />
  );

  const formatStatusColumn = (statusKey: string): React.ReactElement => {
    return <StatusElement statusValue={statusKey} />;
  };

  const formatScoreColumn = (technologyArea, score): React.ReactElement => {
    return <span>{technologyArea ? (score ? score : "Not scored") : ""}</span>;
  };

  const getTenantLink = (itemId): React.ReactElement => {
    if (tenant === TenantTypesEnum.MVP) {
      return (
        <Link href={`/${tenant}/applications/${itemId}`} target="_blank">
          {itemId}
        </Link>
      );
    } else {
      return <Link href="#">{itemId}</Link>;
    }
  };

  const applicationColumn = [
    {
      key: "id",
      name: "ID",
      ariaLabel: "Application ID",
      fieldName: "id",
      minWidth: 20,
      maxWidth: 30,
      isResizable: true,
      tenants: [TenantTypesEnum.MLSA, TenantTypesEnum.MVP, TenantTypesEnum.RD],
      onRender: (item: IApplicationListRowData): React.ReactElement => {
        return getTenantLink(item.id);
      },
    },
    {
      key: "firstName",
      name: "First Name",
      ariaLabel: "Applicant's first name",
      fieldName: "firstName",
      isResizable: true,
      minWidth: 70,
      maxWidth: 100,
      tenants: [TenantTypesEnum.MLSA, TenantTypesEnum.MVP, TenantTypesEnum.RD],
    },
    {
      key: "lastName",
      name: "Last Name",
      ariaLabel: "Applicant's last name",
      fieldName: "lastName",
      isResizable: true,
      minWidth: 70,
      maxWidth: 100,
      tenants: [TenantTypesEnum.MLSA, TenantTypesEnum.MVP, TenantTypesEnum.RD],
    },
    {
      key: "technologyArea1",
      name: "Technology Area 1",
      ariaLabel: "Technology Area 1",
      fieldName: "technologyArea1",
      minWidth: 180,
      isResizable: true,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "technologyArea1Status",
      name: "Status (TA1)",
      ariaLabel: "Status of Technology Area 1",
      fieldName: "technologyArea1Status",
      isResizable: true,
      minWidth: 80,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "score1",
      name: "Score (TA1)",
      ariaLabel: "Score of user who is assigned to review this application",
      fieldName: "score1",
      isResizable: true,
      minWidth: 80,
      tenants: [TenantTypesEnum.MVP],
      onRender: (item: IApplicationListRowData): React.ReactElement => {
        return formatScoreColumn(item.technologyArea1, item.score1);
      },
    },
    {
      key: "technologyArea1StatusReason",
      name: "Reason (TA1)",
      ariaLabel: "Reason of Technology Area 1",
      fieldName: "technologyArea1StatusReason",
      isResizable: true,
      minWidth: 90,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "assignedTo1",
      name: "Assigned To (TA1)",
      ariaLabel: "Email of user who is assigned to review this application",
      fieldName: "assignedTo1",
      isResizable: true,
      minWidth: 150,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "awardCategory1",
      name: "Award Category 1",
      ariaLabel: "Award Category 1",
      fieldName: "awardCategory1",
      minWidth: 200,
      isResizable: true,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "technologyArea2",
      name: "Technology Area 2",
      ariaLabel: "Technology Area 2",
      fieldName: "technologyArea2",
      minWidth: 180,
      isResizable: true,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "technologyArea2Status",
      name: "Status (TA2)",
      ariaLabel: "Status of Technology Area 2",
      fieldName: "technologyArea2Status",
      isResizable: true,
      minWidth: 80,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "score2",
      name: "Score (TA2)",
      ariaLabel: "Score of user who is assigned to review this application",
      fieldName: "score2",
      isResizable: true,
      minWidth: 80,
      tenants: [TenantTypesEnum.MVP],
      onRender: (item: IApplicationListRowData): React.ReactElement => {
        return formatScoreColumn(item.technologyArea2, item.score2);
      },
    },
    {
      key: "technologyArea2StatusReason",
      name: "Reason (TA2)",
      ariaLabel: "Reason of Technology Area 2",
      fieldName: "technologyArea2StatusReason",
      isResizable: true,
      minWidth: 90,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "assignedTo2",
      name: "Assigned To (TA2)",
      ariaLabel: "Email of user who is assigned to review this application",
      fieldName: "assignedTo2",
      isResizable: true,
      minWidth: 150,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "awardCategory2",
      name: "Award Category 2",
      ariaLabel: "Award Category 2",
      fieldName: "awardCategory2",
      minWidth: 200,
      isResizable: true,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "flagReason",
      name: "Reason for Flag",
      ariaLabel: "Reason for Flag",
      fieldName: "flagReason",
      isResizable: true,
      minWidth: 150,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "countryRegion",
      name: "Country",
      ariaLabel: "Country",
      fieldName: "countryRegion",
      isResizable: true,
      minWidth: 70,
      maxWidth: 100,
      tenants: [TenantTypesEnum.MLSA, TenantTypesEnum.RD],
    },
    {
      key: "countryRegion",
      name: "Country",
      ariaLabel: "Country",
      fieldName: "countryRegion",
      isResizable: true,
      minWidth: 70,
      maxWidth: 100,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "applicationType",
      name: "Application Type",
      ariaLabel: "Applicant's application type",
      fieldName: "applicationType",
      isResizable: true,
      minWidth: 100,
      maxWidth: 120,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "cohort",
      name: "Cohort",
      ariaLabel: "Cohort",
      fieldName: "cohort",
      isResizable: true,
      minWidth: 55,
      maxWidth: 100,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "scoreDueInDays",
      name: "Score Due in (days)",
      ariaLabel: "Score Due in (days)",
      fieldName: "scoreDueInDays",
      isResizable: true,
      minWidth: 70,
      maxWidth: 100,
      tenants: [TenantTypesEnum.MVP],
    },
    {
      key: "submittedOn",
      name: "Submitted on",
      ariaLabel: "The date application was submitted for review",
      fieldName: "submittedOn",
      isResizable: true,
      minWidth: 70,
      maxWidth: 100,
      tenants: [TenantTypesEnum.MLSA, TenantTypesEnum.MVP, TenantTypesEnum.RD],
      onRender: function formattedDate(item: IApplicationListRowData): React.ReactElement {
        // If DateSubmitted is null, will get empty string from backend.
        // In this case, we just leave it as blank.
        return <>{item.submittedOn === "" ? "" : formatDateEu(new Date(item.submittedOn))}</>;
      },
    },
    {
      key: "industryFocus",
      name: "Industry Focus",
      ariaLabel: "Industry Focus",
      fieldName: "industryFocus",
      isResizable: true,
      minWidth: 70,
      maxWidth: 150,
      tenants: [TenantTypesEnum.RD],
    },
    {
      key: "technicalExpertise",
      name: "Technology Expertise",
      ariaLabel: "Technology Expertise",
      fieldName: "technicalExpertise",
      isResizable: true,
      minWidth: 70,
      maxWidth: 150,
      tenants: [TenantTypesEnum.RD],
    },
    {
      key: "applicationStatus",
      name: "Application Status",
      ariaLabel: "Applicant's application status",
      fieldName: "applicationStatus",
      isResizable: true,
      minWidth: 110,
      maxWidth: 130,
      tenants: [TenantTypesEnum.MLSA, TenantTypesEnum.RD],
      onRender: (item: IApplicationListRowData): React.ReactElement => {
        return formatStatusColumn(item.applicationStatus);
      },
    },
    {
      key: "reviewStatus",
      name: "Review",
      ariaLabel: "Applicant's review status",
      fieldName: "reviewStatus",
      isResizable: true,
      minWidth: 80,
      maxWidth: 100,
      tenants: [TenantTypesEnum.MLSA, TenantTypesEnum.RD],
      onRender: (item: IApplicationListRowData): React.ReactElement => {
        return formatReviewStatus(item.reviewStatus);
      },
    },
    {
      key: "modifiedDate",
      name: "Modified Date",
      ariaLabel: "The date on which the application was last modified or reviewed",
      fieldName: "modifiedDate",
      isResizable: true,
      minWidth: 80,
      maxWidth: 100,
      tenants: [TenantTypesEnum.MLSA, TenantTypesEnum.RD],
      onRender: function formattedDate(item: IApplicationListRowData): React.ReactElement {
        const date = new Date(item.submittedOn);
        return <>{formatDateEu(date)}</>;
      },
    },
    {
      key: "modifiedBy",
      name: "Modified By",
      ariaLabel: "Email of user who last modified this application",
      fieldName: "modifiedBy",
      isResizable: true,
      minWidth: 80,
      maxWidth: 100,
      tenants: [TenantTypesEnum.MLSA, TenantTypesEnum.RD],
    },
  ];

  const [columns, setColumns] = useState<IColumn[]>([]);

  const onClickEvent = (application: IApplicationListRowData): void => {
    switch (tenant) {
      case TenantTypesEnum.MVP:
        actions.getSelectedApplicationById(application.id, tenant);
        break;
      case TenantTypesEnum.MLSA:
      case TenantTypesEnum.RD:
      default:
        actions.updateSelectedApplicationEmail(application.email);
        setIsModalOpen(true);
        break;
    }
  };

  const onModalClose = (): void => {
    setIsModalOpen(false);
  };

  const onRenderColumn = (
    item: IApplicationListRowData,
    index?: number,
    column?: IColumn
  ): React.ReactElement => {
    const fieldContent = item[column?.fieldName as keyof IApplicationListRowData];
    switch (column?.key) {
      case "id":
        return <StyledSpan>{fieldContent}</StyledSpan>;
      case "industryFocus":
        return <span>{(fieldContent as string[]).join(", ")}</span>;
      case "technicalExpertise":
        return <span>{(fieldContent as string[]).join(", ")}</span>;
      default:
        return <span>{fieldContent}</span>;
    }
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): React.ReactElement => {
    const selectedApplication = props && (props.item as IApplicationListRowData);

    // Allows for calculated values to be included in the item data rendered by list
    const calculatedItemProps = {
      ...selectedApplication,
      ...{},
    };

    return defaultRender({
      ...props,
      item: { ...calculatedItemProps },
      onClick: () => !!selectedApplication && onClickEvent(selectedApplication),
    });
  };

  const onSortingUpdate = (sortColumn?: string, sortDescending?: boolean): void => {
    actions.applicationSearchSortingUpdate(sortColumn, sortDescending);
  };

  const onColumnClickHandler = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    applicationColumn.forEach((col: IColumn) => {
      if (col.key == column.key) {
        const isSortedDescending = !col.isSortedDescending;
        col.isSortedDescending = isSortedDescending;
        col.isSorted = true;
        onSortingUpdate(col.fieldName, isSortedDescending);
      } else {
        col.isSorted = false;
        col.isSortedDescending = true;
      }
    });
  };

  useEffect(() => {
    const newColumns: IColumn[] = [];
    const sortingColumns = [
      SortingColumns.FirstName,
      SortingColumns.LastName,
      SortingColumns.Country,
      "awardCategory1",
      "technologyArea1",
      "awardCategory2",
      "technologyArea2",
      "score1",
      "score2",
    ];
    applicationColumn?.forEach((col: IColumn) => {
      if (sortingColumns.filter((c) => c === col.fieldName).length > 0) {
        col.onColumnClick = onColumnClickHandler;
      }
      newColumns.push(col as IColumn);
    });
    setColumns(
      newColumns
        .filter((x: ApplicationColumn) => x.tenants?.includes(tenant))
        .map((x) => x as IColumn)
    );
  }, []);

  if (isLoadingApplications) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  } else if (loadApplicationsError !== "") {
    return <div>{loadApplicationsError}</div>;
  }

  const onGetUserProfiles = (ids: number[]): void => {
    const deselectedItems = userProfileInfoResults.filter(
      (x) => !ids.includes(x.applicationFormId)
    );
    deselectedItems.forEach((x) => (x["isSelected"] = false));
    const selectedItems = userProfileInfoResults.filter((x) => ids.includes(x.applicationFormId));
    selectedItems.forEach((x) => (x["isSelected"] = true));
    const allItems = [...deselectedItems, ...selectedItems];
    setUserProfileInfoResults(allItems);
    const userProfilesTemp: IApplicationUserProfileInformation[] = [];

    ids.forEach(async (id) => {
      const userProfileInfoResult = userProfileInfoResults.find((x) => x.applicationFormId == id);
      if (!userProfileInfoResult) {
        const response = await getProfileInformationByApplicationId(id, TenantTypesEnum.MVP);

        if (response.ok) {
          const userProfileInfoResultsTemp = userProfileInfoResults;
          const item = response.parsedBody as unknown as IApplicationUserProfileInformation;
          item["isSelected"] = true;
          userProfilesTemp.push(item);
        }
      }
      if (ids.length == userProfilesTemp.length) {
        setUserProfileInfoResults([...selectedItems, ...userProfilesTemp]);
      }
    });
  };

  const selection = new Selection({
    onSelectionChanged: () => {
      const ids: number[] = selection.getSelection().map((item) => (item as IApplication).id);
      onGetUserProfiles(ids);
    },
  });

  const handlePageChange = (page: number): void => {
    actions.updateCurrentPage(page);
    selection.setAllSelected(false); // Clear selection when the page changes
    setUserProfileInfoResults([]); // Clear user profile results
  };

  return (
    <>
      {tenant === TenantTypesEnum.MVP ? <CommandBar items={commandBarItems} /> : null}
      <DetailsList
        items={data}
        columns={columns}
        getKey={(item: any): string => `${(item as IApplication).id}`}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        onRenderRow={onRenderRow}
        onRenderItemColumn={onRenderColumn}
        enterModalSelectionOnTouch={true}
        styles={detailListStyles}
        selectionMode={SelectionMode.multiple}
        checkboxVisibility={
          tenant === TenantTypesEnum.MVP ? CheckboxVisibility.always : CheckboxVisibility.hidden
        }
        selection={selection}
        selectionPreservedOnEmptyClick={true}
      />
      <PaginationRow>
        <Pagination
          total={filteredCount}
          onChange={(page: number) => handlePageChange(page)}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            actions.updatePageSize(op?.key as number);
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
      <Modal
        titleAriaId="title"
        isOpen={isModalOpen}
        isBlocking={false}
        containerClassName={modalStyles}
        topOffsetFixed
      >
        <ModalHeader>
          <Text block styles={modalHeaderStyles}>
            Application Details
          </Text>
          <IconButton
            styles={iconButtonStyles}
            iconProps={{ iconName: "Cancel" }}
            ariaLabel="Close popup modal"
            onClick={onModalClose}
          />
        </ModalHeader>
        <ModalContent>
          <ApplicationDetails closeModal={onModalClose} />
        </ModalContent>
      </Modal>
      <ApplicationBulkAssignSidePanel
        isPanelOpen={isAssignPanelOpen}
        onSubmitSuccess={(): ActivitiesEventsActions.ActivitiesEventsActionTypes =>
          dispatch(
            ActivitiesEventsActions.getUserProfileBySelectedApplicationId(
              Number(userProfileInfoResults?.[0]?.applicationFormId),
              TenantTypesEnum.MVP
            )
          )
        }
        setIsPanelOpen={setIsAssignPanelOpen}
        applicationFormIds={userProfileInfoResultsSelected?.map((info) => info.applicationFormId)}
      />
    </>
  );
};

/**
 * Maps StudentsContainer to redux state
 * @param {AppState} state The App state.
 */
const mapStateToProps = (state: AppState, ownProps: IApplicationsListProps & any) => ({
  data: ownProps.data,
  isLoadingApplications: isLoadingApplications(state.applicationState),
  loadApplicationsError: getLoadApplicationsError(state.applicationState),
  formSubmittingComplete: false,
  currentPage: getCurrentPage(state.applicationState),
  pageSize: getPageSize(state.applicationState),
  searchText: getSearchText(state.applicationState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (
  dispatch: Dispatch<
    ApplicationActions.ApplicationActionTypes | ActivitiesEventsActions.ActivitiesEventsActionTypes
  >
) => ({
  actions: {
    updateCurrentPage: bindActionCreators(ApplicationActions.updateCurrentPage, dispatch),
    updatePageSize: bindActionCreators(ApplicationActions.updatePageSize, dispatch), //Create new application action and bind new application
    updateSelectedApplicationEmail: bindActionCreators(
      ApplicationActions.updateSelectedApplicationEmail,
      dispatch
    ),
    getSelectedApplicationById: bindActionCreators(
      ActivitiesEventsActions.getUserProfileBySelectedApplicationId,
      dispatch
    ),
    applicationSearchSortingUpdate: bindActionCreators(
      ApplicationActions.applicationSearchSortingUpdate,
      dispatch
    ),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationsList);
